/* eslint-disable react-hooks/exhaustive-deps */
import { Modal, ModalContent, ModalOverlay, useBreakpointValue, useDisclosure } from "@chakra-ui/react";
import type { ModalProps } from "@chakra-ui/react";
import { useRouter } from "next/router";
import type { ReactNode } from "react";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { trackCustomEvent, trackUAEvent } from "utils/tracking";
import type { ModalContextProps, ModalContextProviderProps } from "./ModalContext.d";

export const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const ModalContextProvider = ({ children, gaEvent, uaEvent }: ModalContextProviderProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalContent, setModalContent] = useState<ReactNode>(null);
  const [size, setModalSize] = useState<ModalProps["size"]>();
  const router = useRouter();

  const handleHashChange = () => {
    onClose();
    trackUAEvent(uaEvent);
    trackCustomEvent(gaEvent);

    window.removeEventListener("hashchange", handleHashChange);
  };

  useEffect(
    () => () => {
      window.removeEventListener("hashchange", handleHashChange);
    },
    []
  );

  const openModal: ModalContextProps["openModal"] = useCallback(
    (content, modalSize = "sm") => {
      if (content) {
        onOpen();
        setModalContent(content);
        setModalSize(modalSize);
        const path = router.asPath.includes("#modal") ? `${router.asPath}` : `${router.asPath}#modal`;
        router.replace(path, null, { shallow: true });
        window.addEventListener("hashchange", handleHashChange);
      }
    },
    [onOpen, router]
  );

  const closeModal = useCallback(() => {
    onClose();
    const path = router.asPath.replace("#modal", "");
    router?.replace(path, null, { shallow: true });
    trackUAEvent(uaEvent);
    trackCustomEvent(gaEvent);
    window.removeEventListener("hashchange", handleHashChange);
  }, [onClose, gaEvent, router.asPath]);

  const value = useMemo(
    () => ({
      openModal,
      closeModal,
      onClose,
    }),
    [openModal, closeModal]
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        size={useBreakpointValue({ base: "full", md: size })}
        isCentered={false}
      >
        <ModalOverlay bg="rgba(69, 84, 115, 0.7)" />
        <ModalContent
          borderRadius="md"
          overflow="auto"
          minHeight={{ base: "auto", md: "initial" }}
          mt={{ base: 0, md: size === "full" ? 4 : 12 }}
          {...(size === "full" ? { mb: 0 } : null)}
          {...(size === "xl" ? { maxWidth: "755px" } : null)}
        >
          {modalContent}
        </ModalContent>
      </Modal>
    </ModalContext.Provider>
  );
};
